<template>
  <div v-if="dataConfig" class="query-builder-rule" :class="{ 'query-builder-rule-lg': isSpecialRule }" :id="vm.id">
    <template v-if="!isSpecialRule">
      <div class="query-builder-rule-selected-searchType">
        {{ $t("AdvancedSearch.SearchType." + vm.localModel.searchType) }}
      </div>
      <div class="criteria-manipulation">
        <div class="preselection-list" v-if="dataConfig?.uiType === 'preselectList'">
          <MSelect
            v-model="vm.localModel.attributeId"
            :items="sortedSelectionList"
            textField="name"
            :value-field="currentIdFieldName ?? 'id'"
            data-cy="rule-preselection" />
        </div>
        <div class="rule-ops-value">
          <div class="query-builder-rule-ops-selection" v-if="operators">
            <MSelect class="w-100" v-model="vm.localModel.operator" :items="validatedOperators" :value-field="'id'" data-cy="rule-operator" />
          </div>
          <div class="query-builder-rule-value-selection">
            <component
              :is="getValueSelectionComponentByType()"
              v-model="vm.localModel.value"
              :items="dataConfig?.selection"
              :can-be-none="isDateBoundarySearchType"
              @delete-rule="emits('deleteRule', { id: vm.id })"
              :class="{ 'advanced-search-user-selector': isUserRuleType }">
              <template v-if="isUserRuleType" #default="{ entry }">
                <UserProfileSelectorItem :username="entry.name" :id="entry.userId" :has-image="entry.hasImage"></UserProfileSelectorItem>
              </template>
              <template v-if="isUserRuleType" #button-content="{ entry }">
                <UserProfileSelectorItem :username="entry?.name" :id="entry?.userId" :has-image="entry.hasImage"></UserProfileSelectorItem>
              </template>
            </component>
          </div>
        </div>
      </div>
      <div class="criteria-meta-info">
        <div class="query-builder-rule-language-selection">
          <MSelect
            v-if="dataConfig?.languageDependent"
            v-model="vm.localModel.dataLanguage"
            :items="languageSelection"
            textField="name"
            valueField="id"
            data-cy="rule-language" />
        </div>
        <div class="query-builder-rule-actions">
          <b-btn class="btn-icon mono" @click="emits('deleteRule', { id: vm.id })" data-cy="rule-clear">
            <Icon icon="Close" />
          </b-btn>
        </div>
      </div>
    </template>
    <template v-else>
      <PreselectionRule
        v-model="vm.localModel"
        :language-required="!!dataConfig?.languageDependent"
        :additional-operator-support="dataConfig"
        @delete-rule="emits('deleteRule', { id: vm.id })" />
    </template>
  </div>
</template>

<script setup lang="ts">
import MSelect from "@/BasicComponentLibrary/MSelect.vue";
import { getTranslatedSelectionOpsList } from "@AdvancedSearch/Util/opsLookUp";
import Icon from "@common/components/Icon.vue";
import UserProfileSelectorItem from "@common/components/UserProfileSelectorItem.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { AllowedOps, FetchableData, QueryBuilderRule } from "@medapro-types/AdvancedSearch/components/QueryBuilder";
import { RootState } from "@medapro-types/common/rootstate";
import { Guid } from "@util/guid";
import { naturalSort } from "@util/naturalSort";
import _ from "lodash";
import { Ref, computed, inject, nextTick, onMounted, reactive, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useState } from "vuex-composition-helpers";
import AttributeGroupRule from "./QueryRules/AttributeGroupRule.vue";
import BoolRule from "./QueryRules/BoolRule.vue";
import CatalogPathRule from "./QueryRules/CatalogPathRule.vue";
import DateRule from "./QueryRules/DateRule.vue";
import DateTimeRule from "./QueryRules/DateTimeRule.vue";
import DropDownRule from "./QueryRules/DropDownRule.vue";
import EditableDropDownRule from "./QueryRules/EditableDropDownRule.vue";
import NumberRule from "./QueryRules/NumberRule.vue";
import PreselectionRule from "./QueryRules/PreselectionRule.vue";
import StringRule from "./QueryRules/StringRule.vue";

//#region General
const { t: $t } = useI18n();
const props = defineProps<{ value: QueryBuilderRule; path: string }>();
const { metadata } = useState<RootState>(["metadata"]);
const emits = defineEmits(["input", "deleteRule", "refreshBackground"]);
//#endregion

//#region ViewModel
type ViewModel = {
  id: string;
  localModel: QueryBuilderRule;
};
const vm = reactive<ViewModel>({
  id: props.value.ruleId ?? Guid.new(),
  localModel: props.value
});

const isUserRuleType = computed(() => {
  return vm.localModel.searchType === "assignedTo" || vm.localModel.searchType === "createdBy" || vm.localModel.searchType === "changedBy";
});

const isDateBoundarySearchType = computed(() => {
  return vm.localModel.searchType === "validFrom" || vm.localModel.searchType === "validTo";
});

//#endregion

//#region FetchedData
const fetchedData = inject<Ref<FetchableData>>("fetchedData");
const dataConfig = computed(() => unref(fetchedData)?.find((x) => x.searchType === vm.localModel.searchType));
const { localizedLanguages } = useContentLanguages();
//#endregion

function isNegatedOp(op: string | null | undefined): boolean {
  if (op == null) return false;
  return op.toLowerCase().includes("not");
}

//#region Update vModel
watch(
  vm.localModel,
  (nv) => {
    emits("input", nv);
  },
  { immediate: true, deep: true }
);
//#endregion

//#region Rule Value Selection
const isSpecialRule = computed(() => {
  return dataConfig.value?.uiType == "preselectTree";
});

const validatedOperators = computed(() => {
  // currently only necessary if there is a language-dependent rule
  if (dataConfig?.value?.languageDependent === false) {
    return operators.value;
  }
  const areNegatedOperatorsDisabled = vm.localModel.dataLanguage === "";
  return areNegatedOperatorsDisabled ? operators.value.filter((obj) => !isNegatedOp(obj.id)) : operators.value;
});

const operators = computed(() => {
  if (dataConfig.value?.uiType && dataConfig.value?.uiType != "preselectList") {
    if (dataConfig.value?.uiType === "date") {
      if (props.value.value === "") return getTranslatedSelectionOpsList(dataConfig.value?.uiType).filter((o) => o.id === "equals" || o.id === "equalsNot");
    }
    return getTranslatedSelectionOpsList(dataConfig.value?.uiType);
  }
  const foundEntry = getPreselectionEntry();
  return foundEntry === "Double" ? getTranslatedSelectionOpsList("numeric") : getTranslatedSelectionOpsList("alphanumeric");
});

const getValueSelectionComponentByType = () => {
  let foundEntry;
  switch (dataConfig.value?.uiType) {
    case "selectionList":
      return DropDownRule;
    case "searchableList":
      return EditableDropDownRule;
    case "numeric":
      return NumberRule;
    case "logical":
      return BoolRule;
    case "date":
      return DateRule;
    case "datetime":
      return DateTimeRule;
    case "catalogNode":
      return CatalogPathRule;
    case "attributeGroup":
      return AttributeGroupRule;
    case "preselectList":
      foundEntry = getPreselectionEntry();
      return foundEntry === "Double" ? NumberRule : StringRule;
    default:
      return StringRule;
  }
};
//#endregion

//#region PreselectionEntry
function getPreselectionEntry() {
  const foundEntry = dataConfig.value?.selection.find((x) => x[currentIdFieldName.value ?? "id"] == vm.localModel.attributeId);
  return !foundEntry ? null : foundEntry.dataType;
}
//#region Languages
const languageSelection = computed(() => {
  const result = [
    ...(unref(localizedLanguages)?.map((x) => ({
      id: x.languageCode,
      name: x.localizedName
    })) ?? [])
  ];

  if (!isNegatedOp(vm.localModel.operator)) {
    result.unshift({ id: "", name: `<${$t("inAnyLanguage")}>` });
  }

  return result;
});
//#endregion

//#region PreselectionList

const sortedSelectionList = computed(() => {
  const allValue: Record<string, string | null> = { name: `<${$t("all")}>` };
  const idKey = unref(currentIdFieldName);

  if (idKey) allValue[idKey] = null;

  return dataConfig.value ? [allValue, ...naturalSort(dataConfig.value?.selection, (x) => x.name)] : [];
});

const currentIdFieldName = computed<string | null>(() => {
  if (dataConfig.value?.uiType === "preselectList") {
    const firstElement = _.first(dataConfig.value?.selection);
    if (firstElement) {
      const foundIdName = Object.keys(firstElement).find((x) => x.endsWith("Id"));
      if (foundIdName) return foundIdName;
    }
  }
  return null;
});

//#endregion

//#region LanguageSelection
watch(
  () => dataConfig.value?.languageDependent,
  (nv) => {
    if (nv && vm.localModel.dataLanguage === undefined) {
      vm.localModel.dataLanguage = metadata?.value?.contentLanguages.enabled[0].languageCode;
    }
  },
  { immediate: true }
);
//#endregion

//#region LifeCycle

onMounted(() => {
  if (!vm.localModel.ruleId) {
    vm.localModel.ruleId = vm.id;
  }

  nextTick(() => {
    if (!vm.localModel.operator && dataConfig.value?.uiType != "preselectTree") {
      vm.localModel.operator = (_.first(operators.value)?.id ?? "equals") satisfies AllowedOps;
    }

    if (dataConfig.value?.uiType === "preselectList" && !vm.localModel.attributeId) {
      const firstElement = _.first(sortedSelectionList.value);
      if (firstElement) {
        const foundIdName = Object.keys(firstElement).find((x) => x.endsWith("Id"));
        if (foundIdName) vm.localModel.attributeId = firstElement[foundIdName];
      }
    }
  });
});

//#endregion
</script>
